import axios from "axios";
import { API_BASE_URL } from "../constants/configuration";

// Cookie carries auth. `withCredentials: true` sends it on every call.
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Read the CSRF cookie value (non-HttpOnly) so we can echo it back as a header.
// Double-submit: backend rejects any state-changing request whose
// X-Csrf-Token header doesn't match the `csrf` cookie.
function getCsrfTokenFromCookie() {
  const m = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

axiosInstance.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = getCsrfTokenFromCookie();
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-Csrf-Token"] = token;
    }
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config;

    // CSRF auto-recovery: the backend now returns `reason: "cookie-missing"`
    // when the `csrf` cookie isn't on the request. That happens after the
    // 12h cookie expiry, after a deploy that changed COOKIE_DOMAIN, or if
    // the user cleared cookies for the API host only. Fetch a fresh cookie
    // via /auth/csrf (GET, csrf-exempt, verifyJwt-gated) and retry the
    // original request exactly once. "mismatch" / "header-missing" are NOT
    // retried — those usually indicate stale tabs or a real attack.
    if (status === 403 && error?.response?.data?.reason === "cookie-missing" && cfg && !cfg.__csrfRetried) {
      try {
        cfg.__csrfRetried = true;
        await axiosInstance.get("auth/csrf");
        return axiosInstance.request(cfg);
      } catch (_e) {
        // Refresh failed — fall through to the 401 handler if applicable.
      }
    }

    if (status === 401) {
      localStorage.clear();
      if (window.location.pathname !== "/") {
        window.location.replace("/");
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
